import {
  CylinderUnitRowVm,
  EngineReportCylinderUnit,
  EngineReportCylinderUnits,
  EngineReportCylinderUnitsVm,
} from '@navstation-portal/reporting/data-model/engine-report';
import { Units } from '@navstation-portal/shared/data-model/ui';
import { EngineTypes, PrimeMoverTypes, VesselSpec } from '@navstation-portal/vessels/data-model/vessel';
import { TooltipSeverity, getHardValidation, getRangeTooltip, isOutOfRange } from '../details/range-tooltip.utils';

const DEFAULT_CYLINDER_COUNT = 3;

const EXHAUST_TEMPERATURE_LIMITS = { min: 0, max: 600 } as const;
const EXHAUST_TEMPERATURE_SOFT_LIMITS = { min: 150, max: 480 } as const;
const PRESSURE_LIMITS = { min: 0, max: 200 } as const;
const FUEL_PUMP_INDEX_LIMITS = { min: 10, max: 200 } as const;
const P_INDICATED_LIMITS = { min: 0 } as const;
const CW_OUTLET_TEMPERATURE_LIMITS = { min: 0, max: 170 } as const;
const PISTON_COOLING_TEMPERATURE_LIMITS = { min: 0, max: 80 } as const;

export function getCylinderCount(mainEngine: number | undefined, vesselSpec: VesselSpec | undefined): number {
  const numOfCylinders = vesselSpec?.engine?.me_engines?.[mainEngine ?? 0]?.n_cylinders;
  if (!numOfCylinders || numOfCylinders === 0) return DEFAULT_CYLINDER_COUNT;
  return numOfCylinders;
}

function isFourStroke(vesselSpec: VesselSpec | undefined): boolean {
  const info = vesselSpec?.information;
  return (
    info?.prime_mover_type === PrimeMoverTypes.FOUR_STROKE_ENGINE ||
    info?.engine_type === EngineTypes.FOUR_STROKE_ENGINE
  );
}

function buildRow(
  unit: EngineReportCylinderUnit,
  unitNumber: number,
  pCompAndPistonCoolingRequired: boolean
): CylinderUnitRowVm {
  const exhaust = getHardValidation(
    unit.exhaustTemperature,
    EXHAUST_TEMPERATURE_LIMITS,
    'Exhaust Temperature',
    Units.degreesCelsius,
    true
  );
  const pMax = getHardValidation(unit.pMax, PRESSURE_LIMITS, 'P Max', Units.bar, true);
  const pComp = getHardValidation(unit.pComp, PRESSURE_LIMITS, 'P Comp', Units.bar, pCompAndPistonCoolingRequired);
  const fuelPumpIndex = getHardValidation(unit.fuelPumpIndex, FUEL_PUMP_INDEX_LIMITS, 'Fuel Pump Index', '', true);
  const pIndicated = getHardValidation(unit.pIndicated, P_INDICATED_LIMITS, 'P Indicated', Units.bar);
  const cwOutlet = getHardValidation(
    unit.cwOutletTemperature,
    CW_OUTLET_TEMPERATURE_LIMITS,
    'CW Outlet Temperature',
    Units.degreesCelsius,
    true
  );
  const pistonCooling = getHardValidation(
    unit.pistonCoolingTemperature,
    PISTON_COOLING_TEMPERATURE_LIMITS,
    'Piston Cooling Temperature',
    Units.degreesCelsius,
    pCompAndPistonCoolingRequired
  );

  return {
    ...unit,
    unitNumber,
    exhaustTemperatureInvalid: exhaust.invalid,
    exhaustTemperatureInvalidTooltip: exhaust.tooltip,
    exhaustTemperatureWarning:
      !exhaust.invalid && isOutOfRange(unit.exhaustTemperature, EXHAUST_TEMPERATURE_SOFT_LIMITS),
    exhaustTemperatureWarningTooltip: getRangeTooltip(
      'Exhaust Gas Temperature',
      EXHAUST_TEMPERATURE_SOFT_LIMITS,
      Units.degreesCelsius,
      TooltipSeverity.Warning
    ),
    pMaxInvalid: pMax.invalid,
    pMaxInvalidTooltip: pMax.tooltip,
    pCompInvalid: pComp.invalid,
    pCompInvalidTooltip: pComp.tooltip,
    fuelPumpIndexInvalid: fuelPumpIndex.invalid,
    fuelPumpIndexInvalidTooltip: fuelPumpIndex.tooltip,
    pIndicatedInvalid: pIndicated.invalid,
    pIndicatedInvalidTooltip: pIndicated.tooltip,
    cwOutletTemperatureInvalid: cwOutlet.invalid,
    cwOutletTemperatureInvalidTooltip: cwOutlet.tooltip,
    pistonCoolingTemperatureInvalid: pistonCooling.invalid,
    pistonCoolingTemperatureInvalidTooltip: pistonCooling.tooltip,
  };
}

export function buildCylinderUnitsVm(
  cylinderUnits: EngineReportCylinderUnits | undefined,
  mainEngine: number | undefined,
  vesselSpec: VesselSpec | undefined
): EngineReportCylinderUnitsVm {
  const count = getCylinderCount(mainEngine, vesselSpec);
  const pCompAndPistonCoolingRequired = !isFourStroke(vesselSpec);

  const rows = Array.from({ length: count }, (_, index) =>
    buildRow(cylinderUnits?.cyl?.[index] ?? {}, index + 1, pCompAndPistonCoolingRequired)
  );

  return {
    rows,
    pCompAndPistonCoolingRequired,
  };
}
